import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Injectable()
export class LoggingInterceptor implements HttpInterceptor {
  private callCounts: { [key: string]: { count: number; lastCall: number; calls: number[] } } = {};
  private readonly WARNING_THRESHOLD = 5; // Advertencia después de 5 llamadas
  private readonly ERROR_THRESHOLD = 10; // Error después de 10 llamadas
  private readonly TIME_WINDOW = 60000; // 1 minuto en milisegundos

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const url = request.url;
    const now = Date.now();

    // Inicializar contador para esta URL si no existe
    if (!this.callCounts[url]) {
      this.callCounts[url] = { count: 0, lastCall: 0, calls: [] };
    }

    // Limpiar llamadas antiguas (más de 1 minuto)
    this.callCounts[url].calls = this.callCounts[url].calls.filter(
      timestamp => now - timestamp < this.TIME_WINDOW
    );

    // Agregar nueva llamada
    this.callCounts[url].calls.push(now);
    this.callCounts[url].count = this.callCounts[url].calls.length;
    this.callCounts[url].lastCall = now;

    // Verificar si es una llamada al endpoint de beneficios
    if (url.includes('/ipa/beneficios')) {
      this.monitorBenefitsCalls(url, this.callCounts[url]);
    }

    // Verificar si es una llamada al endpoint de carrusel
    if (url.includes('/ipa/carrusel')) {
      this.monitorCarouselCalls(url, this.callCounts[url]);
    }

    return next.handle(request).pipe(
      tap(() => {
        // Log exitoso
        console.log(`✅ HTTP ${request.method} ${url} - Llamada #${this.callCounts[url].count}`);
      }),
      catchError((error: HttpErrorResponse) => {
        // Log de error
        console.error(`❌ HTTP Error ${request.method} ${url}:`, error.status, error.statusText);
        return throwError(() => error);
      })
    );
  }

  private monitorBenefitsCalls(url: string, callData: { count: number; lastCall: number; calls: number[] }) {
    const { count, lastCall, calls } = callData;
    const now = Date.now();

    // Verificar llamadas muy frecuentes (más de 1 por segundo)
    if (calls.length >= 2) {
      const timeBetweenCalls = calls[calls.length - 1] - calls[calls.length - 2];
      if (timeBetweenCalls < 1000) {
        console.warn(`⚠️ BENEFICIOS: Llamadas muy frecuentes detectadas (${timeBetweenCalls}ms entre llamadas)`);
        console.warn(`   URL: ${url}`);
        console.warn(`   Posible problema: Múltiples inicializaciones o eventos de hover sin debounce`);
      }
    }

    // Verificar muchas llamadas en poco tiempo
    if (count >= this.ERROR_THRESHOLD) {
      console.error(`🚨 BENEFICIOS: Demasiadas llamadas HTTP detectadas!`);
      console.error(`   URL: ${url}`);
      console.error(`   Total de llamadas en 1 minuto: ${count}`);
      console.error(`   Posible bucle infinito o problema de cache`);
      console.error(`   Última llamada: ${new Date(lastCall).toLocaleTimeString()}`);
    } else if (count >= this.WARNING_THRESHOLD) {
      console.warn(`⚠️ BENEFICIOS: Muchas llamadas HTTP detectadas`);
      console.warn(`   URL: ${url}`);
      console.warn(`   Total de llamadas en 1 minuto: ${count}`);
      console.warn(`   Considerar optimizar cache o reducir llamadas`);
    }

    // Verificar si las llamadas están muy espaciadas (posible problema de cache)
    if (calls.length >= 3) {
      const avgTimeBetweenCalls = (calls[calls.length - 1] - calls[0]) / (calls.length - 1);
      if (avgTimeBetweenCalls > 30000) { // Más de 30 segundos entre llamadas
        console.info(`ℹ️ BENEFICIOS: Llamadas espaciadas detectadas (promedio: ${Math.round(avgTimeBetweenCalls / 1000)}s)`);
        console.info(`   URL: ${url}`);
        console.info(`   Posible problema: Cache no está funcionando correctamente`);
      }
    }
  }

  private monitorCarouselCalls(url: string, callData: { count: number; lastCall: number; calls: number[] }) {
    const { count, lastCall, calls } = callData;

    // Verificar llamadas muy frecuentes (más de 1 por segundo)
    if (calls.length >= 2) {
      const timeBetweenCalls = calls[calls.length - 1] - calls[calls.length - 2];
      if (timeBetweenCalls < 1000) {
        console.warn(`⚠️ CARRUSEL: Llamadas muy frecuentes detectadas (${timeBetweenCalls}ms entre llamadas)`);
        console.warn(`   URL: ${url}`);
        console.warn(`   Posible problema: Múltiples inicializaciones o autoplay sin cache`);
      }
    }

    // Verificar muchas llamadas en poco tiempo
    if (count >= this.ERROR_THRESHOLD) {
      console.error(`🚨 CARRUSEL: Demasiadas llamadas HTTP detectadas!`);
      console.error(`   URL: ${url}`);
      console.error(`   Total de llamadas en 1 minuto: ${count}`);
      console.error(`   Posible bucle infinito o problema de cache`);
      console.error(`   Última llamada: ${new Date(lastCall).toLocaleTimeString()}`);
    } else if (count >= this.WARNING_THRESHOLD) {
      console.warn(`⚠️ CARRUSEL: Muchas llamadas HTTP detectadas`);
      console.warn(`   URL: ${url}`);
      console.warn(`   Total de llamadas en 1 minuto: ${count}`);
      console.warn(`   Considerar optimizar cache o reducir llamadas`);
    }
  }

  // Método para obtener estadísticas de llamadas (útil para debugging)
  getCallStatistics(): { [key: string]: { count: number; lastCall: number; calls: number[] } } {
    const now = Date.now();
    const stats: { [key: string]: { count: number; lastCall: number; calls: number[] } } = {};

    Object.keys(this.callCounts).forEach(url => {
      const callData = this.callCounts[url];
      // Limpiar llamadas antiguas
      callData.calls = callData.calls.filter(timestamp => now - timestamp < this.TIME_WINDOW);
      callData.count = callData.calls.length;

      stats[url] = { ...callData };
    });

    return stats;
  }

  // Método para limpiar estadísticas (útil para testing)
  clearStatistics(): void {
    this.callCounts = {};
    console.log('🧹 LoggingInterceptor: Estadísticas limpiadas');
  }
}
